import React, { useEffect, useState } from 'react';
import { useLabels, usePrefs } from 'wsm-common-data';
import PropTypes from 'prop-types';
import ContactLinksList from './ContactLinksList';
import DepartmentHoursTabs from './DepartmentHoursTabs';
import AddressCard from './AddressCard';

const RichContent = ({
	addInfo,
	richContentToDisplay,
	pageAlias,
	windowId,
	handleLinkClick
}) => {
	const labels = useLabels();
	const {
		addressHeadingLabel,
		addressClasses,
		addressGetDirectionsLabel,
		hoursTabsHeaderLabel
	} = usePrefs();
	const [contactLinksAriaLabelledById, setContactLinksAriaLabelledById] =
		useState('');
	const [addressAriaLabelledById, setAddressAriaLabelledById] = useState('');
	const [hoursTabsAriaLabelledById, setHoursTabsAriaLabelledById] =
		useState('');
	const [hoursTabsId, setHoursTabsId] = useState('');

	useEffect(() => {
		// This effect only runs once to setup specific data for this component
		// Generate unique ids
		setContactLinksAriaLabelledById(
			`${pageAlias}_${windowId}_ContactLinkAria`
		);
		setAddressAriaLabelledById(`${pageAlias}_${windowId}_AddressAria`);
		setHoursTabsAriaLabelledById(`${pageAlias}_${windowId}_HoursTabsAria`);
		setHoursTabsId(`${pageAlias}_${windowId}_HourTabsId`);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Contact Links
	const renderContactLinksList = () => {
		const { phoneNumbers, email = '' } = addInfo;
		return (
			<ContactLinksList
				phoneNumbers={phoneNumbers}
				email={email}
				richContentToDisplay={richContentToDisplay}
				ariaLabelledById={contactLinksAriaLabelledById}
				handleLinkClick={handleLinkClick}
			/>
		);
	};

	// Address
	const renderAddress = () => {
		const hideLabel = richContentToDisplay.some(
			(item) =>
				[
					'email',
					'phone-sales',
					'phone-service',
					'phone-parts',
					'phone-commercial'
				].indexOf(item) > -1
		);
		const {
			dealerInfo: { name, address1, city, state, postalCode },
			mapLink
		} = addInfo;
		return (
			<AddressCard
				name={name}
				address1={address1}
				city={city}
				state={state}
				postalCode={postalCode}
				getDirectionsLabel={labels.get(addressGetDirectionsLabel)}
				getDirectionsUrl={mapLink}
				label={!hideLabel ? labels.get(addressHeadingLabel) : ''}
				ariaLabelledById={addressAriaLabelledById}
				addressClasses={addressClasses}
				handleLinkClick={handleLinkClick}
			/>
		);
	};

	// Hours
	const renderHours = () => {
		const { hours } = addInfo;
		const departments = richContentToDisplay
			.filter((richContentName) => richContentName.startsWith('hours'))
			.map((richContentName) => richContentName.split('-')[1]);
		return (
			<DepartmentHoursTabs
				departments={departments}
				departmentHours={hours}
				label={labels.get(hoursTabsHeaderLabel)}
				labels={labels}
				ariaLabelledById={hoursTabsAriaLabelledById}
				tabId={hoursTabsId}
				handleLinkClick={handleLinkClick}
			/>
		);
	};

	const renderFunctions = {
		'phone-sales': renderContactLinksList,
		'phone-service': renderContactLinksList,
		'phone-parts': renderContactLinksList,
		'phone-commercial': renderContactLinksList,
		email: renderContactLinksList,
		address: renderAddress,
		'hours-sales': renderHours,
		'hours-service': renderHours,
		'hours-parts': renderHours,
		'hours-commercial': renderHours
	};

	const renderedComponents = new Set();

	return (
		<div
			className="modern-search-richcontent"
			data-testid="modern-search-richcontent"
		>
			{richContentToDisplay.map((item) => {
				const renderFunction = renderFunctions[item];
				if (renderFunction && !renderedComponents.has(renderFunction)) {
					renderedComponents.add(renderFunction);
					return (
						<React.Fragment key={item}>
							{renderFunction()}
						</React.Fragment>
					);
				}
				return null;
			})}
		</div>
	);
};
RichContent.propTypes = {
	addInfo: PropTypes.shape(),
	richContentToDisplay: PropTypes.arrayOf(PropTypes.string),
	pageAlias: PropTypes.string,
	windowId: PropTypes.string,
	handleLinkClick: PropTypes.func
};

export default RichContent;
